import { useState } from "react";
import { useNavigate, useSearchParams } from "@/lib/router-compat";
import { Loader2, ShieldCheck, Building2 } from "lucide-react";
import { postTrackingEvent, buildTrackingPayload } from "@/lib/tracking";
import { trackLeadConversion } from "@/lib/conversions";

interface BcbsilHeroFormProps {
  employerName?: string | null;
}

/**
 * Compact, conversion-first eligibility form for the hero of /bcbs-illinois-saint.
 * Asks only what's needed to verify BCBSIL coverage and start a consultation.
 */
export function BcbsilHeroForm({ employerName }: BcbsilHeroFormProps = {}) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [firstName, setFirstName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [whoIsThisFor, setWhoIsThisFor] = useState("Me");
  const [consent, setConsent] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const quizResult = searchParams.get("quiz_result") || "";
  const bcbsilRelationship = searchParams.get("bcbsil_relationship") || "";

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!firstName.trim()) {
      setError("Please enter your first name.");
      return;
    }
    if (!email.trim() || !email.includes("@")) {
      setError("Please enter a valid email address.");
      return;
    }
    if (!phone.trim()) {
      setError("Please enter your phone number.");
      return;
    }
    if (!consent) {
      setError(
        "Please agree to the contact terms to request a benefits check.",
      );
      return;
    }

    setIsSubmitting(true);

    try {
      const trackingPayload = buildTrackingPayload(
        "fb-ads-bcbsil-saint",
        "SAINT BCBSIL Hero Opt-in",
      );
      trackingPayload.formData = {
        first_name: firstName.trim(),
        email: email.trim(),
        phone: phone.trim(),
      };
      trackingPayload.formLabels = {
        first_name: "First Name",
        email: "Email",
        phone: "Phone",
      };

      postTrackingEvent(trackingPayload, {
        customFields: {
          MYQlMr3lrCpW6TonKqy8: {
            value: whoIsThisFor,
            label: "Who is this for?",
          },
          N4F73KEeils3e4crrgNS: {
            value: quizResult,
            label: "Quiz Result",
          },
          vPP16D6zMI7Sn45erJRk: {
            value:
              bcbsilRelationship ||
              (employerName ? `Employee at ${employerName}` : ""),
            label: "BCBSIL Relationship",
          },
          ...(employerName
            ? {
                Lo0LKbbbbzL5W37GsvVy: {
                  value: employerName,
                  label: "Employer Name",
                },
              }
            : {}),
        },
      });

      trackLeadConversion("fb-ads-bcbsil-saint", "SAINT BCBSIL Hero Opt-in");

      if (typeof window !== "undefined") {
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
          event: "generate_lead",
          form_name: "SAINT BCBSIL Hero Opt-in",
          form_id: "fb-ads-bcbsil-saint",
          form_category: "benefits_verification",
          thank_you_path: "/thank-you/saint-bcbsil",
        });
      }

      navigate("/thank-you/saint-bcbsil");
    } catch (err) {
      console.error("Form submit error:", err);
      navigate("/thank-you/saint-bcbsil");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form
      onSubmit={handleSubmit}
      className="space-y-3.5 text-left bg-white rounded-2xl border border-border shadow-xl p-5 sm:p-6"
    >
      <div className="flex items-center gap-2 pb-1">
        <ShieldCheck className="w-5 h-5 text-accent shrink-0" />
        <h3 className="text-base sm:text-lg font-bold text-primary tracking-tight">
          {employerName
            ? `Check your ${employerName} BCBSIL coverage — free`
            : "Check your BCBSIL coverage — free"}
        </h3>
      </div>
      {employerName && (
        <div className="flex items-center gap-1.5 px-2.5 py-1 rounded bg-muted text-primary text-xs font-medium">
          <Building2 className="w-3.5 h-3.5 text-accent shrink-0" />
          <span>
            Employer plan verified: <strong>{employerName}</strong>
          </span>
        </div>
      )}
      <p className="text-xs text-muted-foreground -mt-1">
        Takes about 60 seconds. A care coordinator reaches out within one
        business day.
      </p>

      {error && (
        <div className="p-2.5 text-xs bg-red-50 text-red-700 border border-red-200 rounded-lg">
          {error}
        </div>
      )}

      <input type="hidden" name="quiz_result" value={quizResult} />
      <input
        type="hidden"
        name="bcbsil_relationship"
        value={bcbsilRelationship}
      />

      <div>
        <input
          type="text"
          required
          value={firstName}
          onChange={(e) => setFirstName(e.target.value)}
          placeholder="First name"
          aria-label="First name"
          className="w-full h-12 px-4 rounded-lg border border-border text-sm text-primary focus:outline-hidden focus:ring-2 focus:ring-accent focus:border-transparent bg-muted/40"
        />
      </div>

      <div>
        <input
          type="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="Email address"
          aria-label="Email address"
          className="w-full h-12 px-4 rounded-lg border border-border text-sm text-primary focus:outline-hidden focus:ring-2 focus:ring-accent focus:border-transparent bg-muted/40"
        />
      </div>

      <div>
        <input
          type="tel"
          required
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
          placeholder="Phone number"
          aria-label="Phone number"
          className="w-full h-12 px-4 rounded-lg border border-border text-sm text-primary focus:outline-hidden focus:ring-2 focus:ring-accent focus:border-transparent bg-muted/40"
        />
      </div>

      <div>
        <select
          value={whoIsThisFor}
          onChange={(e) => setWhoIsThisFor(e.target.value)}
          aria-label="Who is this for?"
          className="w-full h-12 px-4 rounded-lg border border-border text-sm text-primary focus:outline-hidden focus:ring-2 focus:ring-accent focus:border-transparent bg-muted/40"
        >
          <option value="Me">This is for me</option>
          <option value="A family member on my plan">
            A family member on my plan
          </option>
          <option value="Both">Both me and a family member</option>
        </select>
      </div>

      <label className="flex items-start gap-2.5 cursor-pointer pt-0.5">
        <input
          type="checkbox"
          required
          checked={consent}
          onChange={(e) => setConsent(e.target.checked)}
          className="mt-0.5 h-4 w-4 rounded border-gray-300 text-accent focus:ring-accent shrink-0"
        />
        <span className="text-[11px] text-muted-foreground leading-relaxed">
          I agree to be contacted by Neuro Wellness TMS Centers of America by
          phone, text, and email about my consultation. Consent isn&apos;t
          required to receive care. Msg &amp; data rates may apply.
        </span>
      </label>

      <button
        type="submit"
        disabled={isSubmitting}
        className="w-full h-12 px-6 rounded-xl font-semibold text-white bg-accent hover:bg-accent/90 transition-colors shadow-sm active:scale-[0.99] flex items-center justify-center gap-2 cursor-pointer disabled:opacity-75"
      >
        {isSubmitting ? (
          <>
            <Loader2 className="w-5 h-5 animate-spin" />
            <span>Checking...</span>
          </>
        ) : (
          <span>Check my coverage</span>
        )}
      </button>

      <p className="text-[11px] text-center text-muted-foreground">
        Free consultation over Zoom · No cost, no obligation
      </p>
    </form>
  );
}
